import * as React from 'react';
import { FC } from 'react';
import Field from './Field';
import TargetSelect from './TargetSelect';
import RowTarget from './RowTarget';
import CryptoWithdrawalDetails from './CryptoWithdrawalDetails';
import { useContext } from './useFormContext';
import { UpdateValidationState } from './useValidationState';

const CisBankWithdrawalFields: FC<{ updateValidationState: UpdateValidationState }> = ({ updateValidationState }) => {
  const { locales, extra } = useContext();
  const cisBanks = (extra.cis_banks || []) as Array<{ id: string; name: string }>;
  const defaultBank = cisBanks[0]?.id || '';

  return (
    <div className="rs-row">
      <div className="rs-column-small-12">
        <label htmlFor="bank_name">{locales.bank_name}:</label>
        <select name="bank_name" id="bank_name" required defaultValue={defaultBank}>
          {cisBanks.map(({ id, name }) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <div className="rs-column-small-12">
        <RowTarget updateValidationState={updateValidationState} validation="required" />
      </div>
    </div>
  );
};

const BitgetpayCryptoDetails: FC = () => {
  const { locales } = useContext();

  return (
    <div className="rs-row">
      <div className="rs-column-small-12">
        <label>{locales.crypto_currency_label}:</label>
        <div className="radio-group">
          {Object.entries((locales.crypto_currency_options || {}) as Record<string, string>).map(([code, label]) => (
            <span key={code}>
              <input type="radio" name="crypto_currency" id={`crypto_currency_${code}`} value={code} required />
              <label htmlFor={`crypto_currency_${code}`}>{label}</label>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const BitgetpayWithdrawalDetails: FC<{ updateValidationState: UpdateValidationState }> = ({
  updateValidationState,
}) => (
  <>
    <BitgetpayCryptoDetails />
    <RowTarget updateValidationState={updateValidationState} validation="required" />
  </>
);

const MercadoPagoWithdrawalDetails: FC<{ updateValidationState: UpdateValidationState }> = ({
  updateValidationState,
}) => {
  const { locales } = useContext();

  return (
    <div className="rs-row">
      <div className="rs-column-small-12">
        <label>{locales.account_type_label}:</label>
        <div className="radio-group">
          {Object.entries((locales.account_type_options || {}) as Record<string, string>).map(([code, label]) => (
            <span key={code}>
              <input type="radio" name="account_type" id={`account_type_${code}`} value={code} required />
              <label htmlFor={`account_type_${code}`}>{label}</label>
            </span>
          ))}
        </div>
      </div>
      <div className="rs-column-small-12">
        <Field
          label={locales.target_label}
          name="target"
          validation="html5"
          pattern="^\d{22}$"
          placeholder={locales.target_placeholder}
          updateValidationState={updateValidationState}
        />
      </div>
    </div>
  );
};

const TaxIdDetails: FC<{
  updateValidationState: UpdateValidationState;
}> = ({ updateValidationState }) => {
  const { locales } = useContext();

  return (
    <>
      <Field
        label={locales.target_label}
        name="target"
        validation="required"
        updateValidationState={updateValidationState}
        placeholder={locales.target_placeholder}
      />
      <Field
        label={locales.tax_id_label}
        name="tax_id"
        validation="required"
        updateValidationState={updateValidationState}
        placeholder={locales.tax_id_placeholder}
      />
    </>
  );
};

const PixWithdrawalDetails: FC<{
  updateValidationState: UpdateValidationState;
}> = ({ updateValidationState }) => {
  const { locales } = useContext();

  return (
    <div className="rs-row">
      <div className="rs-column-small-12">
        <RowTarget validation="html5" pattern="^\d{11}$" updateValidationState={updateValidationState} />
      </div>
      <div className="rs-column-small-12">
        <Field
          label={locales.bank_name_label}
          name="bank_name"
          validation="required"
          updateValidationState={updateValidationState}
        />
      </div>
      <div className="rs-column-small-12">
        <Field
          label={locales.bank_branch_label}
          name="bank_branch"
          validation="required"
          updateValidationState={updateValidationState}
        />
      </div>
      <div className="rs-column-small-12">
        <Field
          label={locales.bank_account_number_label}
          name="bank_account_number"
          validation="required"
          updateValidationState={updateValidationState}
        />
      </div>
      <div className="rs-column-small-12">
        <Field
          label={locales.ispb_number_label}
          name="ispb_number"
          validation="required"
          updateValidationState={updateValidationState}
        />
      </div>
      <div className="rs-column-small-12">
        <label htmlFor="type_account" className="rs-label">
          {locales.type_account_label}
        </label>
        <select id="type_account" name="type_account" className="rs-input" required>
          {Object.entries((locales.type_account_options || {}) as Record<string, string>).map(([code, label]) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const BrazilWithdrawalDetails: FC<{
  updateValidationState: UpdateValidationState;
}> = ({ updateValidationState }) => {
  const { locales } = useContext();

  return (
    <>
      <div className="rs-column-small-12">
        <label htmlFor="payment_method" className="rs-label">
          {locales.payment_method_label}
        </label>
        <select id="payment_method" name="payment_method" className="rs-input" defaultValue="CPF">
          {Object.entries((locales.payment_method_options || {}) as Record<string, string>).map(([code, label]) => (
            <option key={code} value={code.toUpperCase()}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <TaxIdDetails updateValidationState={updateValidationState} />
    </>
  );
};

const AppendFields: FC<{ vendorCurrency: string; updateValidationState: UpdateValidationState }> = ({
  vendorCurrency,
  updateValidationState,
}) => {
  const { key, locales, extra } = useContext();

  switch (key) {
    case 'card_deposit': {
      const warning = (locales.warnings as unknown as Record<string, string>)?.[vendorCurrency];
      return warning ? (
        <div className="rs-column-small-12">
          <p className="error">{warning}</p>
        </div>
      ) : null;
    }

    case 'mobile_withdrawal':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="target_extra[service_provider]">{locales.service_provider}:</label>
            <select name="target_extra[service_provider]" required>
              {locales.service_providers.map((code) => (
                <option key={code} value={code}>
                  {locales.service_provider_names[code] || code}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="phoneWithCode" />
          </div>
        </div>
      );

    case 'bullet_cash_withdrawal/bullet_cash':
      return (
        <div className="rs-column-small-12">
          <Field
            label={locales.pan_label}
            name="pan"
            validation="required"
            updateValidationState={updateValidationState}
            placeholder={locales.pan_placeholder}
          />
        </div>
      );

    case 'bullet_cash_deposit/bullet_cash':
      return (
        <>
          <div className="rs-column-small-12">
            <Field
              label={locales.pan_label}
              name="pan"
              validation="required"
              updateValidationState={updateValidationState}
              placeholder={locales.pan_placeholder}
            />
          </div>
          <div className="rs-column-small-12">
            <Field
              label={locales.pass_label}
              name="pass"
              validation="required"
              updateValidationState={updateValidationState}
              type="password"
            />
          </div>
        </>
      );

    case 'argentina_bank_deposit/apipay':
    case 'pix_deposit':
    case 'pix_deposit/smilepayz':
    case 'pix_deposit/bullet_pay':
      return (
        <div className="rs-column-small-12">
          <Field
            label={locales.pix_account_label || 'PIX account (CPF/CNPJ)'}
            name="target"
            validation="html5"
            required
            pattern="^(\d{11}|\d{14})$"
            placeholder={locales.pix_account_placeholder || 'CPF: 11 digits, CNPJ: 14 digits'}
            updateValidationState={updateValidationState}
          />
          {locales.pix_account_hint && <p className="rs-text-size_small">{locales.pix_account_hint}</p>}
        </div>
      );
    case 'cis_bank_withdrawal':
      return <CisBankWithdrawalFields updateValidationState={updateValidationState} />;

    case 'binancepay_withdrawal/ecp2025':
    case 'monetix_withdrawal/ecp2025':
    case 'sepa_withdrawal/ecp2025':
      return <RowTarget updateValidationState={updateValidationState} validation="required" />;

    case 'argentina_bank_withdrawal':
      return <TaxIdDetails updateValidationState={updateValidationState} />;

    case 'mercado_pago_withdrawal/mercurio':
      return <MercadoPagoWithdrawalDetails updateValidationState={updateValidationState} />;

    case 'bybit_withdrawal':
    case 'bybit_withdrawal/ecp2025':
    case 'gcash_withdrawal':
    case 'gcash_withdrawal/ecp2025':
    case 'paymaya_withdrawal':
    case 'paymaya_withdrawal/ecp2025':
      return <RowTarget updateValidationState={updateValidationState} validation="html5" pattern="^\d+$" />;

    case 'crypto_withdrawal':
      return <CryptoWithdrawalDetails vendorCurrency={vendorCurrency} updateValidationState={updateValidationState} />;

    case 'indonesia_bank_withdrawal/smilepayz':
    case 'indonesia_bank_withdrawal/ecp2025':
    case 'malaysia_withdrawal/ecp2025':
    case 'philippines_withdrawal/ecp2025':
    case 'thailand_withdrawal/ecp2025':
    case 'vietnam_withdrawal/ecp2025':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="target_extra[bank_id]">{locales.bank_id}:</label>
            <select name="target_extra[bank_id]" id="target_extra[bank_id]" required>
              {(locales.banks as unknown as [string, string | number][]).map((bank) => (
                <option key={bank[1]} value={bank[1]}>
                  {bank[0]}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="required" />
          </div>
          <div className="rs-column-small-12">
            <label htmlFor="asia_beneficiary">{locales.beneficiary}:</label>
            <input
              type="text"
              disabled
              readOnly
              name="asia_beneficiary"
              value={`${extra.first_name} ${extra.last_name}`}
            />
            <p className="rs-text-size_small">
              {locales.can_be_changed_in}
              &nbsp;
              <a href="/player">{locales.player_profile}</a>
            </p>
          </div>
        </div>
      );

    case 'ecp2025_deposit/cashtocode':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">
            {(locales.warnings as unknown as Record<string, string>)?.[extra.country]}
          </p>
        </div>
      );

    case 'sbp_deposit':
    case 'sbp_deposit/bova':
      return (
        <div className="rs-column-small-12">
          <label>{locales.bank_name}:</label>
          <div className="radio-group">
            {(locales.service_providers as unknown as string[]).map((code) => (
              <span>
                <input
                  type="radio"
                  name="bank_name"
                  id={`bank_code_${code}`}
                  value={code}
                  required
                  checked={locales.service_providers.length === 1 || undefined}
                />
                <label htmlFor={`bank_code_${code}`}>{locales.service_provider_names[code] || code}</label>
              </span>
            ))}
          </div>
        </div>
      );

    case 'kz_phone_withdrawal/ecp2025':
    case 'sbp_withdrawal':
    case 'sbp_fast_withdrawal':
    case 'spei_withdrawal':
    case 'spei_withdrawal/cardpay':
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="bank_name">{locales.bank_name}:</label>
            <select name="bank_name" id="bank_name" required>
              {(locales.banks as unknown as string[]).map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
          </div>
          <div className="rs-column-small-12">
            <RowTarget updateValidationState={updateValidationState} validation="required" />
          </div>
        </div>
      );

    case 'mexico_withdrawal/smilepayz': {
      return (
        <div className="rs-row">
          <div className="rs-column-small-12">
            <label htmlFor="bank_name">{locales.bank_name}:</label>
            <select name="bank_name" id="bank_name" required>
              {Object.entries((locales as any).banks as Record<string, string>).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="rs-column-small-12">
            <Field
              label={locales.target_label}
              name="target"
              validation="required"
              updateValidationState={updateValidationState}
            />
          </div>
        </div>
      );
    }

    case 'brazil_withdrawal':
    case 'brazil_withdrawal/smilepayz':
      return <BrazilWithdrawalDetails updateValidationState={updateValidationState} />;

    case 'pix_withdrawal/bullet_pay':
      return <PixWithdrawalDetails updateValidationState={updateValidationState} />;

    case 'gopay_withdrawal':
    case 'gopay_withdrawal/smilepayz':
    case 'dana_wallet_withdrawal':
    case 'dana_wallet_withdrawal/smilepayz':
    case 'ovo_wallet_withdrawal':
    case 'ovo_wallet_withdrawal/smilepayz':
      return !extra.phone ? null : (
        <div className="rs-column-small-12">
          <br />
          <p className="rs-text-size_small error">{locales.warning}:</p>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.phone}: </span>
            <span>{extra.phone}</span>
          </div>
        </div>
      );

    case 'pse_wallet_withdrawal':
    case 'pse_wallet_withdrawal/cardpay':
      return (
        <div className="rs-column-small-12">
          <div className="rs-column-small-12">
            <Field
              validation="html5"
              name="identity"
              label={locales.identity_label}
              placeholder={locales.identity_placeholder}
              required
              updateValidationState={updateValidationState}
            />
          </div>
          <br />
          <p className="rs-text-size_small error">{locales.warning}:</p>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.phone}: </span>
            <span>{extra.phone}</span>
          </div>
        </div>
      );

    case 'manual_deposit':
    case 'manual_withdrawal':
    case 'user_withdrawal':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">{locales.warning}</p>
        </div>
      );

    case 'ecopayz_withdrawal':
    case 'neteller_withdrawal/paysafe':
    case 'settlepay_withdrawal':
    case 'skrill_withdrawal':
    case 'webmoney_withdrawal':
    case 'perfect_money_withdrawal':
      return <TargetSelect vendorCurrency={vendorCurrency} updateValidationState={updateValidationState} />;

    case 'interac_withdrawal/ecp2025':
      return (
        <div className="rs-column-small-12">
          <p className="rs-text-size_small error">{locales.warning}:</p>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.email}: </span>
            <span>{extra.email}</span>
          </div>
          <div className="rs-text-size_small error">
            <span style={{ fontWeight: 'bold' }}>{locales.phone}: </span>
            <span>{extra.phone}</span>
          </div>
        </div>
      );

    case 'bitgetpay_deposit':
    case 'bitgetpay_deposit/ecp2025':
      return <BitgetpayCryptoDetails />;

    case 'bitgetpay_withdrawal':
    case 'bitgetpay_withdrawal/ecp2025':
      return <BitgetpayWithdrawalDetails updateValidationState={updateValidationState} />;

    default:
      return null;
  }
};

export default AppendFields;
